import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Bell, X, AlertTriangle, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// VAPID public key for web push (Production)
const VAPID_PUBLIC_KEY = 'BAcuDEhnTDxPNKL3h6oxhf7XaE43m8T8hrKHiXdzT3N3VpIrG0BEXQcPLC2zPzLTf0e7Wm1Ea4aa6WnSPuH_x8I';

function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

function arrayBufferToBase64(buffer) {
  let binary = '';
  const bytes = new Uint8Array(buffer);
  for (let i = 0; i < bytes.byteLength; i++) {
    binary += String.fromCharCode(bytes[i]);
  }
  return window.btoa(binary);
}

function PushNotificationOptIn() {
  const { user, token } = useAuth();
  const [isVisible, setIsVisible] = useState(false);
  const [status, setStatus] = useState('idle'); // idle, requesting, granted, denied
  const [dismissed, setDismissed] = useState(false);

  useEffect(() => {
    // Check if already dismissed or granted
    const dismissedTime = localStorage.getItem('pushNotificationDismissed');
    const granted = localStorage.getItem('pushNotificationGranted');
    
    if (granted === 'true' || (dismissedTime && Date.now() - parseInt(dismissedTime) < 7 * 24 * 60 * 60 * 1000)) {
      return;
    }

    // Check if browser supports notifications
    if (!('Notification' in window)) {
      return;
    }

    // Show prompt after 10 seconds on disaster-hub pages or after 30 seconds elsewhere
    // Show immediately (3 sec) after user registration
    const isDisasterPage = window.location.pathname.includes('disaster');
    const isNewUser = user && !localStorage.getItem('push_shown_to_user_' + user.id);
    const delay = isNewUser ? 3000 : (isDisasterPage ? 5000 : 30000);

    const timer = setTimeout(() => {
      if (Notification.permission === 'default') {
        setIsVisible(true);
        if (user) {
          localStorage.setItem('push_shown_to_user_' + user.id, 'true');
        }
      } else if (Notification.permission === 'granted') {
        localStorage.setItem('pushNotificationGranted', 'true');
      }
    }, delay);

    return () => clearTimeout(timer);
  }, [user]);

  const subscribeToWebPush = async () => {
    if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
      console.log('Push messaging not supported');
      return false;
    }

    try {
      // Register service worker
      const registration = await navigator.serviceWorker.register('/service-worker.js');
      await navigator.serviceWorker.ready;

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY)
      });

      // If user is logged in, send subscription to server
      if (token) {
        await fetch(`${API_URL}/api/push-notifications/subscribe`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`
          },
          body: JSON.stringify({
            endpoint: subscription.endpoint,
            keys: {
              p256dh: arrayBufferToBase64(subscription.getKey('p256dh')),
              auth: arrayBufferToBase64(subscription.getKey('auth'))
            }
          })
        });
      }

      return true;
    } catch (error) {
      console.error('Error subscribing to web push:', error);
      return false;
    }
  };

  const requestPermission = async () => {
    setStatus('requesting');
    
    try {
      const permission = await Notification.requestPermission();
      
      if (permission === 'granted') {
        setStatus('granted');
        localStorage.setItem('pushNotificationGranted', 'true');
        
        // Subscribe to web push
        await subscribeToWebPush();
        
        // Show a test notification
        new Notification('DowUrk Notifications Enabled', {
          body: 'You\'ll receive grant deadlines, event reminders, and emergency alerts.',
          icon: 'https://customer-assets.emergentagent.com/job_equity-focus/artifacts/gzy45arr_Site%20Icon.png',
          tag: 'welcome'
        });
        
        // Subscribe to alerts backend (legacy)
        try {
          await fetch(`${API_URL}/api/resilience/alerts/subscribe-push`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ enabled: true })
          });
        } catch (e) {
          console.log('Legacy push subscription API not available');
        }
        
        setTimeout(() => setIsVisible(false), 2000);
      } else {
        setStatus('denied');
        setTimeout(() => {
          setIsVisible(false);
          handleDismiss();
        }, 2000);
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
      setStatus('denied');
    }
  };

  const handleDismiss = () => {
    setDismissed(true);
    setIsVisible(false);
    localStorage.setItem('pushNotificationDismissed', Date.now().toString());
  };

  if (!isVisible || dismissed) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 50, scale: 0.95 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        exit={{ opacity: 0, y: 50, scale: 0.95 }}
        transition={{ type: "spring", duration: 0.5 }}
        className="fixed bottom-20 left-4 md:left-4 md:w-72 bg-white rounded-xl shadow-xl border border-[#006847]/20 z-[9998] overflow-hidden"
        data-testid="push-notification-optin"
      >
        {/* Compact Header */}
        <div className="bg-gradient-to-r from-[#006847] to-[#004530] px-3 py-2 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Bell className="h-4 w-4" />
              <span className="font-semibold text-sm">Stay Updated</span>
            </div>
            <button
              onClick={handleDismiss}
              className="p-1 hover:bg-white/20 rounded-full transition"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>

        {/* Compact Content */}
        <div className="p-3">
          {status === 'idle' && (
            <>
              <p className="text-gray-600 text-xs mb-2">
                Get grant deadlines, events & emergency alerts.
              </p>
              <div className="flex gap-2">
                <Button 
                  onClick={requestPermission}
                  className="flex-1 bg-[#006847] hover:bg-[#005a3c] text-white h-8 text-xs"
                  size="sm"
                >
                  <Bell className="h-3 w-3 mr-1" />
                  Enable
                </Button>
                <Button 
                  variant="outline"
                  onClick={handleDismiss}
                  className="border-gray-300 h-8 text-xs"
                  size="sm"
                >
                  Later
                </Button>
              </div>
            </>
          )}

          {status === 'requesting' && (
            <div className="flex items-center justify-center py-2">
              <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-[#006847]"></div>
              <span className="ml-2 text-gray-600 text-xs">Requesting...</span>
            </div>
          )}

          {status === 'granted' && (
            <div className="flex items-center justify-center py-2 text-green-600">
              <Check className="h-4 w-4 mr-1" />
              <span className="font-medium text-xs">Enabled!</span>
            </div>
          )}

          {status === 'denied' && (
            <p className="text-gray-600 text-xs text-center py-2">
              Enable later in browser settings.
            </p>
          )}
        </div>
      </motion.div>
    </AnimatePresence>
  );
}

export default PushNotificationOptIn;
